import React, { useState, useEffect, useMemo } from 'react';
import NavigationBar from '../navbar/NavigationBar';
import DownloadIcon from '@mui/icons-material/Download';
import {
    Container,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Button,
    Card,
    Tabs,
    Tab,
    CardContent,
    styled,
    createTheme,
    ThemeProvider,
    Grid,
    CircularProgress,
} from '@mui/material';
import Footer from '../navbar/Footer';
import { getPublications } from '../services/publicationService'; // Import du service

// Création d'un thème personnalisé (inchangé)
const theme = createTheme({
    palette: {
        primary: {
            main: '#2E7D32',
            light: '#4CAF50',
            dark: '#1B5E20',
        },
        secondary: {
            main: '#388E3C',
        },
        background: {
            default: '#f9f9f9',
        },
    },
    typography: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        h5: {
            fontWeight: 600,
        },
        h6: {
            fontWeight: 500,
        },
    },
});

// Styles personnalisés (inchangés)
const StyledTabs = styled(Tabs)({
    borderBottom: '2px solid #e0e0e0',
    marginBottom: '30px',
    '& .MuiTab-root': {
        textTransform: 'none',
        fontWeight: 500,
        fontSize: '0.95rem',
        minWidth: 'auto',
        marginRight: '20px',
        color: '#666',
        '&.Mui-selected': {
            color: '#2E7D32',
            fontWeight: 600,
        },
    },
    '& .MuiTabs-indicator': {
        backgroundColor: '#2E7D32',
        height: '3px',
    },
});

const StyledCard = styled(Card)({
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    height: '100%',
    transition: 'transform 0.2s, box-shadow 0.2s',
    '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
        borderColor: '#4CAF50',
    },
    border: '1px solid #eaeaea',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
});

const StyledCardContent = styled(CardContent)({
    padding: '20px',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
});

// Données statiques pour Projections Démographiques
const projectionsData = [
    {
        title: "Projections Démographiques : Projections par Ages Spécifiques",
        link: "/projectionAgeSpecifique",
        description: "Projections détaillées par âge individuel pour une analyse fine de l'évolution démographique",
    },
    {
        title: "Projections par Groupes d'Ages Quinquennaux",
        link: "/projectionGroupeAgeQuin",
        description: "Projections structurées par tranches d'âge de 5 ans pour les analyses sectorielles et la planification",
    },
    {
        title: "Projections Démographiques Globales",
        link: "/propDemographique",
        description: "Projections démographiques globales et tendances à long terme de la population burundaise",
    },
];

// Données statiques pour Enquêtes
const enquetesData = [
       {
        title: "Enquête Périodique sur la Satisfaction des Utilisateurs des Données Statistiques Officielles",
        link: "/enqueteSatisfaction",
        description: "Evaluation de la satisfaction des utilisateurs des données statistiques officielles pour améliorer la qualité et la pertinence des produits statistiques",
    },
     
    {
        title: "Enquêtes Santé-Démographie",
        link: "/enquenteSanteDemographe",
        description: "Données complètes sur la santé reproductive, la mortalité maternelle et infantile, et les indicateurs démographiques clés",
    },
    {
        title: "Enquêtes Ménages",
        link: "/enqueteMenage",
        description: "Conditions de vie, dépenses des ménages, accès aux services de base et indicateurs socio-économiques",
    },
    {
        title: "Enquêtes Agricoles",
        link: "/enqueteAgricole",
        description: "Statistiques agricoles détaillées : production, superficies cultivées, pratiques culturales et productivité",
    },
    {
        title: "Enquêtes Nutrition",
        link: "/enqueteNutrition",
        description: "État nutritionnel de la population, sécurité alimentaire et pratiques alimentaires",
    },
    {
        title: "Enquêtes Commerce, Transport et Entreprises",
        link: "/enqueteCommerceTransportEntreprise",
        description: "Données sur les échanges commerciaux, transport de marchandises et performances des entreprises",
    },
     {
        title: "Enquêtes sur le soutien financier à la statistique -CRESS",
        link: "/rapportPaysSoutien",
        description: "Evaluation du soutien financier à la statistique nationale par les partenaires au développement, avec analyses des impacts et recommandations pour renforcer le système statistique national",
    },
];

// Données statiques pour Recensements
const recensementData = [
    {
        title: "Résultats du RGPHAE 2024",
        link: "/resultatRGPHAE2024",
        description: "Résultats préliminaires complets du Recensement Général de la Population, de l'Habitat, de l'Agriculture et de l'Elevage de 2024",
    },
    {
        title: "Recensement Général de la Population et de l'Habitat 2008",
        link: "/recensement",
        description: "Données détaillées du recensement de 2008 avec analyses démographiques, sociales et économiques",
    },
    {
        title: "Recensement Général des Cafetiers 2006-2007",
        link: "/RecensementCafe",
        description: "Inventaire complet des exploitations caféières, producteurs et statistiques de production",
    },
];

const Rapport = () => {
    // États pour les données statiques (paginations)
    const [projectionsVisibleCount, setProjectionsVisibleCount] = useState(8);
    const [enquetesVisibleCount, setEnquetesVisibleCount] = useState(8);
    const [recensementVisibleCount, setRecensementVisibleCount] = useState(8);
    const [activeTab, setActiveTab] = useState(0);

    // États pour la section Autres Rapports (API)
    const [autresData, setAutresData] = useState([]);
    const [autresVisibleCount, setAutresVisibleCount] = useState(8);
    const [filterYearAutres, setFilterYearAutres] = useState('all');
    const [loadingAutres, setLoadingAutres] = useState(false);
    const [errorAutres, setErrorAutres] = useState(null);

    // Chargement des données Autres Rapports depuis l'API
    useEffect(() => {
        setLoadingAutres(true);
        getPublications({ type: 'ar', perPage: 1000 })
            .then(res => {
                if (res.error) {
                    setErrorAutres(res.error);
                } else {
                    // Mapper les données reçues
                    const mapped = res.data.map(item => ({
                        title: item.title,
                        link: item.document_url,
                        description: item.description,
                        year: new Date(item.publication_date).getFullYear(),
                    }));
                    // Trier par année décroissante
                    mapped.sort((a, b) => b.year - a.year);
                    setAutresData(mapped);
                }
            })
            .catch(err => {
                setErrorAutres('Erreur lors du chargement des données.');
                console.error(err);
            })
            .finally(() => setLoadingAutres(false));
    }, []);

    // Gestionnaires d'onglets
    const handleChangeTab = (event, newValue) => {
        setActiveTab(newValue);
    };

    // Gestionnaires de chargement pour les sections statiques
    const handleLoadMoreProjections = () => {
        setProjectionsVisibleCount(prev => prev + 6);
    };
    const handleLoadMoreEnquetes = () => {
        setEnquetesVisibleCount(prev => prev + 6);
    };
    const handleLoadMoreRecensement = () => {
        setRecensementVisibleCount(prev => prev + 6);
    };
    const handleLoadMoreAutres = () => {
        setAutresVisibleCount(prev => prev + 6);
    };

    // Filtre pour les Autres Rapports
    const handleFilterChangeAutres = (event) => {
        setFilterYearAutres(event.target.value);
        setAutresVisibleCount(8);
    };

    // Options d'années pour le filtre (basées sur les données chargées)
    const getYearOptions = () => {
        const years = [...new Set(autresData.map(item => item.year))];
        years.sort((a, b) => b - a);
        return years;
    };

    // Données filtrées pour Autres Rapports
    const filterAutresData = () => {
        if (filterYearAutres === 'all') return autresData;
        return autresData.filter(item => item.year === parseInt(filterYearAutres, 10));
    };

    const menuItemStyle = { color: '#2E7D32' };

    // Fonction de rendu générique pour les grilles (adaptée aux données statiques et dynamiques)
    const renderDocumentsGrid = (data, visibleCount, showYear = false, isFileDownload = false, onLoadMore = null) => {
        const visibleData = data.slice(0, visibleCount);

        return (
            <>
                <Grid container spacing={3}>
                    {visibleData.map((document, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                            <StyledCard>
                                <StyledCardContent>
                                    <Typography variant="subtitle1" component="div" style={{
                                        color: theme.palette.primary.dark,
                                        fontWeight: 500,
                                        flex: 1,
                                        fontSize: '1rem',
                                        marginBottom: '12px'
                                    }}>
                                        {document.title}
                                    </Typography>

                                    <Typography variant="body2" color="textSecondary" paragraph style={{
                                        marginBottom: '16px',
                                        color: '#666',
                                        fontSize: '0.875rem',
                                        flex: 1
                                    }}>
                                        {document.description}
                                    </Typography>

                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto' }}>
                                        {showYear && document.year && (
                                            <Typography variant="caption" color="textSecondary" style={{ color: '#888', fontSize: '0.75rem' }}>
                                                Publié en {document.year}
                                            </Typography>
                                        )}
                                        <Button
                                            variant="contained"
                                            startIcon={<DownloadIcon />}
                                            href={document.link}
                                            target={isFileDownload ? "_blank" : "_self"}
                                            rel={isFileDownload ? "noopener noreferrer" : ""}
                                            size="small"
                                            sx={{
                                                backgroundColor: theme.palette.primary.main,
                                                '&:hover': {
                                                    backgroundColor: theme.palette.primary.dark,
                                                },
                                                textTransform: 'none',
                                                fontWeight: 500,
                                                fontSize: '0.75rem',
                                                padding: '6px 16px',
                                                marginLeft: 'auto'
                                            }}
                                        >
                                            {isFileDownload ? "Télécharger le rapport" : "Accéder à la page"}
                                        </Button>
                                    </Box>
                                </StyledCardContent>
                            </StyledCard>
                        </Grid>
                    ))}
                </Grid>

                {visibleCount < data.length && (
                    <Box display="flex" justifyContent="center" style={{ marginTop: '30px' }}>
                        <Button
                            onClick={onLoadMore}
                            variant="outlined"
                            sx={{
                                borderColor: theme.palette.primary.main,
                                color: theme.palette.primary.main,
                                '&:hover': {
                                    borderColor: theme.palette.primary.dark,
                                    backgroundColor: 'rgba(46, 125, 50, 0.04)',
                                },
                                textTransform: 'none',
                                padding: '8px 24px',
                            }}
                        >
                            Afficher plus de rapports
                        </Button>
                    </Box>
                )}
            </>
        );
    };

    return (
        <ThemeProvider theme={theme}>
            <div style={{ backgroundColor: '#f9f9f9', minHeight: '100vh' }}>
                <NavigationBar />
                <Container maxWidth="lg" style={{ paddingTop: '80px', paddingBottom: '40px' }}>
                    <Typography variant="h5" gutterBottom style={{
                        color: theme.palette.primary.dark,
                        marginBottom: '8px',
                        fontWeight: 600
                    }}>
                        Centre de Documentation
                    </Typography>
                    <Typography variant="body1" style={{ color: '#666', marginBottom: '30px' }}>
                        Accédez à l'ensemble des publications statistiques, rapports techniques, enquêtes et recensements produits par l'INSBU
                    </Typography>

                    <div style={{ marginBottom: '30px' }}>
                        <StyledTabs
                            value={activeTab}
                            onChange={handleChangeTab}
                            textColor="primary"
                            indicatorColor="primary"
                            variant="scrollable"
                            scrollButtons="auto"
                        >
                            <Tab label="Projections Démographiques" />
                            <Tab label="Enquêtes" />
                            <Tab label="Recensements" />
                            <Tab label="Autres Rapports" />
                        </StyledTabs>
                    </div>

                    {/* Section Projections Démographiques */}
                    {activeTab === 0 && (
                        <>
                            <Typography variant="h6" gutterBottom style={{ color: theme.palette.primary.main, marginBottom: '16px' }}>
                                Projections Démographiques
                            </Typography>
                            <Typography variant="body1" paragraph style={{ marginBottom: '24px', color: '#555' }}>
                                Les projections démographiques fournissent des estimations futures de la population par âge, sexe et caractéristiques démographiques.
                                Ces données sont essentielles pour la planification du développement, l'allocation des ressources et l'élaboration de politiques sociales et économiques.
                            </Typography>

                            {renderDocumentsGrid(projectionsData, projectionsVisibleCount, false, false, handleLoadMoreProjections)}
                        </>
                    )}

                    {/* Section Enquêtes */}
                    {activeTab === 1 && (
                        <>
                            <Typography variant="h6" gutterBottom style={{ color: theme.palette.primary.main, marginBottom: '16px' }}>
                                Enquêtes Statistiques
                            </Typography>
                            <Typography variant="body1" paragraph style={{ marginBottom: '24px', color: '#555' }}>
                                Les enquêtes statistiques couvrent divers domaines socio-économiques et fournissent des données précieuses pour l'analyse et la prise de décision.
                                Ces enquêtes permettent de suivre les indicateurs clés de développement et d'évaluer l'impact des politiques publiques.
                            </Typography>

                            {renderDocumentsGrid(enquetesData, enquetesVisibleCount, false, false, handleLoadMoreEnquetes)}
                        </>
                    )}

                    {/* Section Recensements */}
                    {activeTab === 2 && (
                        <>
                            <Typography variant="h6" gutterBottom style={{ color: theme.palette.primary.main, marginBottom: '16px' }}>
                                Recensements
                            </Typography>
                            <Typography variant="body1" paragraph style={{ marginBottom: '24px', color: '#555' }}>
                                Les recensements fournissent une photographie détaillée de la population et de l'habitat à un moment donné.
                                Ils constituent la base essentielle pour la planification du développement, l'allocation des ressources
                                et la formulation de politiques publiques adaptées aux besoins réels de la population.
                            </Typography>

                            {renderDocumentsGrid(recensementData, recensementVisibleCount, false, false, handleLoadMoreRecensement)}
                        </>
                    )}

                    {/* Section Autres Rapports (avec données dynamiques) */}
                    {activeTab === 3 && (
                        <>
                            <Typography variant="h6" gutterBottom style={{ color: theme.palette.primary.main, marginBottom: '16px' }}>
                                Autres Rapports
                            </Typography>
                            <Typography variant="body1" paragraph style={{ marginBottom: '24px', color: '#555' }}>
                                Cette section regroupe divers rapports techniques, études méthodologiques, évaluations stratégiques
                                et documents de référence produits par l'INSBU et ses partenaires pour le développement du système statistique national.
                            </Typography>

                            {loadingAutres ? (
                                <Box display="flex" justifyContent="center" my={4}>
                                    <CircularProgress />
                                </Box>
                            ) : errorAutres ? (
                                <Typography color="error" align="center" my={4}>
                                    {errorAutres}
                                </Typography>
                            ) : (
                                <>
                                    <FormControl fullWidth margin="normal" style={{ marginBottom: '30px', width: '320px' }}>
                                        <InputLabel id="year-filter-autres-label" style={{ color: '#666' }}>
                                            Filtrer par année
                                        </InputLabel>
                                        <Select
                                            labelId="year-filter-autres-label"
                                            id="year-filter-autres"
                                            value={filterYearAutres}
                                            label="Filtrer par année"
                                            onChange={handleFilterChangeAutres}
                                            variant="outlined"
                                            sx={{
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#e0e0e0',
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#4CAF50',
                                                },
                                            }}
                                        >
                                            <MenuItem value="all" style={menuItemStyle}>
                                                Tous les rapports
                                            </MenuItem>
                                            {getYearOptions().map(year => (
                                                <MenuItem key={year} value={year} style={menuItemStyle}>
                                                    {year}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    {renderDocumentsGrid(
                                        filterAutresData(),
                                        autresVisibleCount,
                                        true,
                                        true,
                                        handleLoadMoreAutres
                                    )}
                                </>
                            )}
                        </>
                    )}
                </Container>
                <Footer />
            </div>
        </ThemeProvider>
    );
};

export default Rapport;